import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Badge, Spinner, Alert, InputGroup, FormControl } from 'react-bootstrap';
import { Html5QrcodeScanner } from 'html5-qrcode';
import api from '../../services/api';
import './Admin.css';
import '../../Components/NavAuth.css'; // Shared Premium Styles
import './ManageRewards.css'; // Specific page styles

const ManageRewards = () => {
  const [rewards, setRewards] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [isAddingPoints, setIsAddingPoints] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pointsRequired: '',
    category: '',
    stock: '',
    available: true,
    imageFile: null
  });
  const [previewImage, setPreviewImage] = useState(null);

  // États pour QR Code
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [rewardQRCode, setRewardQRCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  // États pour scanner QR Code
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [scannerResult, setScannerResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isProcessingRedemption, setIsProcessingRedemption] = useState(false);
  const [manualQRCode, setManualQRCode] = useState('');
  const qrScannerRef = useRef(null);
  const scannerContainerRef = useRef(null);

  // Fonction de compression d'image (Max 128KB)
  // Fonction de compression d'image (Max 128KB pour Base64 ~ 170000 chars)
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionner si trop grand (max 600px pour sécurité taille)
          const MAX_SIZE = 600;
          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Compression itérative pour atteindre < 128KB (stricte)
          // 128KB = 131072 bytes ~ 174762 caractères base64
          let quality = 0.9;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);

          // On vise ~100KB pour être sûr (env 135000 chars)
          const MAX_CHARS = 135000;

          while (dataUrl.length > MAX_CHARS && quality > 0.1) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }

          resolve(dataUrl);
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  // Fonctions API Modifiées (Base64 + JSON)
  const rewardAPI = {
    getRewards: async () => {
      const response = await api.get('/api/rewards/catalog?all=true');
      return response.data;
    },
    createReward: async (rewardData) => {
      // Envoi direct en JSON (l'image est déjà en Base64 dans rewardData.imageUrl)
      const response = await api.post('/api/rewards', rewardData);
      return response.data;
    },
    updateReward: async (id, rewardData) => {
      const response = await api.put(`/api/rewards/${id}`, rewardData);
      return response.data;
    },
    deleteReward: async (id) => {
      const response = await api.delete(`/api/rewards/${id}`);
      return response.data;
    },
    getStats: async () => {
      const response = await api.get('/api/rewards/stats');
      return response.data;
    },
    getCategories: async () => {
      const response = await api.get('/api/rewards/categories');
      return response.data;
    },
    completePendingRedemption: async (qrCode) => {
      const response = await api.post('/api/rewards/complete-pending-redemption', { qrCode });
      return response.data;
    },
    getClients: async () => {
      const response = await api.get('/api/admin/clients');
      return response.data;
    },
    addPointsToClient: async (clientId, points, description) => {
      const response = await api.post(`/api/admin/clients/${clientId}/add-points`, { points, description });
      return response.data;
    }
  };

  useEffect(() => {
    loadRewards();
    loadCategories();
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const data = await rewardAPI.getClients();
      if (data.success) {
        setClients(data.clients || []);
      }
    } catch (e) { console.error('Erreur chargement clients:', e); }
  };

  const loadRewards = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await rewardAPI.getRewards();
      setRewards(data.rewards || data || []);

      try {
        const statsData = await rewardAPI.getStats();
        setStats(statsData);
      } catch (statsError) {
        console.warn('Stats non dispo');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ... (Keep existing stats calculation if needed, defaulting to API usage primarily)

  const loadCategories = async () => {
    try {
      const data = await rewardAPI.getCategories();
      if (data.categories) {
        setCategories(data.categories.map(c => ({ value: c, label: c.toUpperCase() })));
      }
    } catch (e) { console.error(e); }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Format image invalide');
        return;
      }

      try {
        const base64Image = await compressImage(file);
        setFormData(prev => ({ ...prev, image: base64Image })); // Store Base64 directly
        setPreviewImage(base64Image); // Preview is the same base64
      } catch (err) {
        setError('Erreur traitement image');
      }
    }
  };

  const handleAddNew = () => {
    setEditingReward(null);
    setFormData({
      name: '', description: '', pointsRequired: '', category: 'boissons',
      stock: '', available: true, image: null
    });
    setPreviewImage(null);
    setShowModal(true);
  };

  const handleEdit = (reward) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name,
      description: reward.description,
      pointsRequired: reward.pointsRequired,
      category: reward.category || 'boissons',
      stock: reward.stock || 0,
      available: reward.available !== false,
      image: reward.imageUrl // Keep existing image URL/Base64 if not changed
    });
    setPreviewImage(reward.imageUrl || null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer ?')) {
      await rewardAPI.deleteReward(id);
      loadRewards();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const rewardData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        pointsRequired: parseInt(formData.pointsRequired),
        category: formData.category,
        stock: parseInt(formData.stock),
        available: formData.available
      };

      // Ensure we send imageUrl to the backend
      if (formData.image && formData.image.startsWith('data:')) {
        rewardData.imageUrl = formData.image;
      } else if (editingReward && editingReward.imageUrl) {
        // Keep existing if not changed
        rewardData.imageUrl = editingReward.imageUrl;
      }

      if (editingReward) {
        await rewardAPI.updateReward(editingReward._id || editingReward.id, rewardData);
      } else {
        await rewardAPI.createReward(rewardData);
      }

      setShowModal(false);
      loadRewards();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur sauvegarde');
    }
  };

  const toggleAvailability = async (id) => {
    // ... same logic
    const reward = rewards.find(r => r._id === id || r.id === id);
    if (reward) {
      await rewardAPI.updateReward(id, { available: !reward.available });
      loadRewards();
    }
  };

  // ... Keep scanner logic ...


  const addNewCategory = () => {
    const newCategory = prompt('Entrez le nom de la nouvelle catégorie:');
    if (newCategory && newCategory.trim()) {
      const value = newCategory.toLowerCase().replace(/\s+/g, '_');
      const label = newCategory.charAt(0).toUpperCase() + newCategory.slice(1);

      if (!categories.find(c => c.value === value)) {
        setCategories(prev => [...prev, { value, label }]);
      }

      if (showModal) {
        setFormData(prev => ({ ...prev, category: value }));
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch (e) {
      return '';
    }
  };

  const scanPendingRewardQRCode = async (qrCodeString) => {
    try {
      console.log('🔍 Scan QR Code détecté:', qrCodeString);
      setScannerResult(null);

      const cleanCode = qrCodeString.trim();
      if (!cleanCode) {
        setScannerResult({ success: false, message: 'QR Code vide' });
        return;
      }

      // FORMAT 0: Identification Client Auth (NOUVEAU)
      if (cleanCode.startsWith('CLIENT_AUTH:')) {
        console.log('👤 Identification Client détectée');
        const clientEmail = cleanCode.split(':')[1];
        const clientInfo = clients.find(c => c.email?.toLowerCase() === clientEmail?.toLowerCase());

        setScannerResult({
          success: true,
          message: `Client Identifié : ${clientEmail}`,
          isClientAuth: true,
          clientEmail: clientEmail,
          clientName: clientInfo?.name || 'Client',
          currentPoints: clientInfo?.points || 0
        });
        return;
      }

      // FORMAT 1: Texte simple (REWARD_PENDING:CODE:REWARD_ID:EMAIL:POINTS)
      if (cleanCode.startsWith('REWARD_PENDING:')) {
        console.log('📋 Format: Texte simple détecté');

        const parts = cleanCode.split(':');
        if (parts.length >= 5) {
          const qrData = {
            type: "REWARD_PENDING_REDEMPTION",
            code: parts[1],
            rewardId: parts[2],
            clientEmail: parts[3],
            pointsToDeduct: parseInt(parts[4]) || 0,
            format: "simple"
          };

          console.log('✅ QR Code parsé (simple):', qrData);

          // Appeler directement l'API de complétion
          await completeRedemptionDirect(cleanCode, qrData);
        } else {
          setScannerResult({
            success: false,
            message: 'Format texte invalide (moins de 5 parties)'
          });
        }
        return;
      }

      // FORMAT 2: JSON
      if (cleanCode.startsWith('{') && cleanCode.endsWith('}')) {
        console.log('📋 Format: JSON détecté');

        try {
          const qrData = JSON.parse(cleanCode);

          if (qrData.type !== "REWARD_PENDING_REDEMPTION") {
            setScannerResult({
              success: false,
              message: 'Type incorrect dans le JSON (attendu: REWARD_PENDING_REDEMPTION)'
            });
            return;
          }

          console.log('✅ QR Code parsé (JSON):', qrData);

          // Appeler l'API avec le JSON stringifié
          await completeRedemptionDirect(cleanCode, qrData);

        } catch (e) {
          console.error('❌ Erreur parsing JSON:', e);
          setScannerResult({
            success: false,
            message: 'JSON invalide'
          });
        }
        return;
      }

      // FORMAT 3: Code simple (RWD-XXXX) - Essayer quand même
      if (cleanCode.includes('RWD-')) {
        console.log('📋 Format: Code simple détecté');

        setScannerResult({
          success: true,
          message: 'Code simple détecté. Tentative d\'échange...',
          qrCode: cleanCode,
          canRedeem: true,
          isSimpleCode: true
        });

        // Essayer d'échanger
        try {
          const response = await rewardAPI.completePendingRedemption(cleanCode);

          if (response.success) {
            setScannerResult({
              success: true,
              message: response.message || '✅ Échange réussi !',
              completed: true,
              redemptionData: response.data,
              qrCode: cleanCode
            });

            alert(`✅ Échange réussi !
Client: ${response.data?.client?.email}
Points déduits: ${response.data?.client?.pointsDeducted}
Nouveau solde: ${response.data?.client?.newPoints}`);

          } else {
            setScannerResult({
              success: false,
              message: response.message || 'Erreur lors de l\'échange'
            });
          }
        } catch (error) {
          console.error('❌ Erreur échange code simple:', error);
          setScannerResult({
            success: false,
            message: 'Code simple détecté mais erreur de traitement'
          });
        }
        return;
      }

      // FORMAT INCONNU - Essayer quand même
      console.log('❓ Format: Inconnu, tentative d\'échange...');

      setScannerResult({
        success: false,
        message: 'Format QR Code non reconnu',
        qrCode: cleanCode
      });

    } catch (error) {
      console.error('❌ Erreur scan QR:', error);
      setScannerResult({
        success: false,
        message: error.response?.data?.message || 'Erreur lors du scan'
      });
    }
  };

  // Fonction pour traiter l'échange directement
  const completeRedemptionDirect = async (qrCodeString, qrData) => {
    try {
      setIsProcessingRedemption(true);

      console.log('🎁 Début échange direct:', qrCodeString);

      // Appeler le contrôleur qui déduit automatiquement les points
      const response = await rewardAPI.completePendingRedemption(qrCodeString);

      if (response.success) {
        // Afficher succès
        setScannerResult({
          success: true,
          message: response.message || '✅ Échange complété avec succès !',
          completed: true,
          redemptionData: response.data,
          qrCode: qrCodeString,
          qrData: qrData
        });

        // Recharger les récompenses pour mettre à jour le stock
        await loadRewards();

        // Afficher détails
        setTimeout(() => {
          alert(`✅ Échange terminé !
          
Client: ${response.data?.client?.email}
Points déduits: ${response.data?.client?.pointsDeducted || qrData.pointsToDeduct}
Nouveau solde: ${response.data?.client?.newPoints}
Récompense: ${response.data?.reward?.name}

Le client peut récupérer sa récompense.`);
        }, 500);

      } else {
        throw new Error(response.message || 'Erreur lors de l\'échange');
      }

    } catch (error) {
      console.error('❌ Erreur complétion direct:', error);
      setScannerResult({
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la complétion'
      });
    } finally {
      setIsProcessingRedemption(false);
    }
  };

  // Initialiser le scanner CORRIGÉ
  const initializeScanner = () => {
    // Nettoyer d'abord
    cleanupScanner();

    // Créer un conteneur pour le scanner
    const container = document.getElementById('qr-reader');
    if (!container) return;

    container.innerHTML = '';

    try {
      const scanner = new Html5QrcodeScanner(
        "qr-reader",
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true,
          showZoomSliderIfSupported: true
        },
        false
      );

      scanner.render(
        (decodedText) => {
          console.log('✅ QR Code scanné:', decodedText);

          // Arrêter le scanner
          try {
            scanner.pause(); // Pause au lieu de stop
            document.getElementById('qr-reader').innerHTML = '';
          } catch (err) {
            console.warn('⚠️ Erreur pause scanner:', err);
          }

          setIsScanning(false);

          // Traiter le QR code
          scanPendingRewardQRCode(decodedText);
        },
        (error) => {
          // Ignorer les erreurs de "No QR code found" (c'est normal pendant le scan)
          if (!error.message.includes('No MultiFormat Readers') &&
            !error.message.includes('NotFoundException')) {
            console.warn('⚠️ Erreur scan:', error);
          }
        }
      );

      qrScannerRef.current = scanner;
      setIsScanning(true);

    } catch (error) {
      console.error('❌ Erreur initialisation scanner:', error);
      setIsScanning(false);

      // Mode fallback: input manuel
      container.innerHTML = `
      <div class="alert alert-warning">
        <h5>⚠️ Scanner non disponible</h5>
        <p>Le scanner automatique ne fonctionne pas. Utilisez l'input manuel ci-dessous.</p>
      </div>
    `;
    }
  };

  // Nettoyer le scanner CORRIGÉ
  const cleanupScanner = () => {
    if (qrScannerRef.current) {
      try {
        // Essayer différentes méthodes pour arrêter
        if (typeof qrScannerRef.current.stop === 'function') {
          qrScannerRef.current.stop();
        } else if (typeof qrScannerRef.current.pause === 'function') {
          qrScannerRef.current.pause();
        } else if (typeof qrScannerRef.current.clear === 'function') {
          qrScannerRef.current.clear();
        }

        // Vider le conteneur
        const container = document.getElementById('qr-reader');
        if (container) {
          container.innerHTML = '';
        }
      } catch (error) {
        console.warn('⚠️ Erreur nettoyage scanner:', error);
      }
      qrScannerRef.current = null;
    }

    setIsScanning(false);
  };

  // Scanner manuel avec input
  const handleManualScan = async () => {
    if (!manualQRCode.trim()) {
      setScannerResult({
        success: false,
        message: 'Veuillez entrer un code QR'
      });
      return;
    }

    await scanPendingRewardQRCode(manualQRCode.trim());
    setManualQRCode('');
  };

  // Générer un QR Code pour une récompense (admin seulement)
  const generateRewardQRCode = async (reward) => {
    try {
      setQrLoading(true);

      const timestamp = Date.now();
      const uniqueCode = `RWD-ADMIN-${reward._id.slice(-6)}-${timestamp}`;

      console.log('🎁 Génération QR admin pour:', reward.name);

      const qrData = {
        type: "REWARD_ADMIN",
        code: uniqueCode,
        rewardId: reward._id,
        rewardName: reward.name,
        pointsRequired: reward.pointsRequired,
        category: reward.category || 'general',
        generatedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        location: 'SPINTOSS',
        note: 'QR Code généré par admin - Pour référence uniquement'
      };

      const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(JSON.stringify(qrData))}&color=7B1FA2&bgcolor=FFFFFF&margin=15&format=png&ecc=H`;

      const qrObject = {
        _id: `qr_admin_${timestamp}`,
        code: uniqueCode,
        rewardId: reward._id,
        rewardName: reward.name,
        rewardPoints: reward.pointsRequired,
        qrImageUrl: qrImageUrl,
        qrData: qrData,
        isAdmin: true
      };

      setRewardQRCode(qrObject);
      setShowQRCodeModal(true);

      setError(null);

    } catch (error) {
      console.error('❌ Erreur génération QR admin:', error);
      setError(`Erreur: ${error.message}`);
    } finally {
      setQrLoading(false);
    }
  };

  // Fonction pour ajouter des points manuellement
  const handleAddPoints = async (email) => {
    const client = clients.find(c => c.email?.toLowerCase() === email.toLowerCase());
    if (!client) {
      alert("Client non trouvé dans la liste locale. Veuillez actualiser.");
      return;
    }

    const pointsStr = window.prompt(`Ajouter combien de points à ${client.name || client.email} ?`, "10");
    if (pointsStr === null) return;

    const points = parseInt(pointsStr);
    if (isNaN(points) || points <= 0) {
      alert("Veuillez entrer un nombre de points valide.");
      return;
    }

    const description = window.prompt("Description (optionnel) :", "Cadeau Admin");

    try {
      setIsAddingPoints(true);
      const response = await rewardAPI.addPointsToClient(client._id, points, description || "Cadeau Admin");
      if (response.success) {
        alert(`✅ ${points} points ajoutés à ${client.name || client.email}`);
        setScannerResult(null);
        await loadClients(); // Recharger pour avoir le nouveau solde si besoin
      }
    } catch (err) {
      alert("Erreur lors de l'ajout des points: " + (err.response?.data?.message || err.message));
    } finally {
      setIsAddingPoints(false);
    }
  };

  // Fonction pour ouvrir le modal scanner
  const openScannerModal = () => {
    setShowScannerModal(true);
    setScannerResult(null);
    setManualQRCode('');

    // Démarrer le scanner après un court délai
    setTimeout(() => {
      initializeScanner();
    }, 500);
  };

  // Fonction pour fermer le modal scanner
  const closeScannerModal = () => {
    cleanupScanner();
    setShowScannerModal(false);
    setScannerResult(null);
    setManualQRCode('');
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Gestion Rewards</h2>
          <p className="text-muted mb-0">Administration du catalogue</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="info" onClick={openScannerModal} className="text-white">
            📷 Scanner Client
          </Button>
          <Button variant="primary" onClick={handleAddNew}>
            + Nouvelle Récompense
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* STATS ROW */}
      {stats && (
        <Row className="mb-4 text-center">
          <Col md={3}>
            <Card className="p-3 shadow-sm border-0 bg-primary text-white">
              <h3>{stats.stats.totalRewards}</h3>
              <small>Total Récompenses</small>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="p-3 shadow-sm border-0 bg-info text-white">
              <h3>{stats.stats.totalStock}</h3>
              <small>Stock Total</small>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="p-3 shadow-sm border-0 bg-warning text-dark">
              <h3>{stats.stats.avgPoints}</h3>
              <small>Points Moyens</small>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="p-3 shadow-sm border-0 bg-danger text-white">
              <h3>{stats.stats.lowStock}</h3>
              <small>Stock Critique</small>
            </Card>
          </Col>
        </Row>
      )}

      {/* REWARDS TABLE */}
      <Card className="shadow-sm border-0">
        <Card.Body>
          <Table responsive hover className="align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '80px' }}>Image</th>
                <th>Nom</th>
                <th>Points</th>
                <th>Catégorie</th>
                <th>Stock</th>
                <th>État</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rewards.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    Aucune récompense trouvée
                  </td>
                </tr>
              ) : (
                rewards.map((reward) => (
                  <tr key={reward._id || reward.id}>
                    <td>
                      <img
                        src={reward.imageUrl || "https://via.placeholder.com/50"}
                        alt={reward.name}
                        style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => { e.target.src = "https://via.placeholder.com/50?text=KDO"; }}
                      />
                    </td>
                    <td>
                      <div className="fw-bold">{reward.name}</div>
                      <small className="text-muted d-block text-truncate" style={{ maxWidth: '200px' }}>
                        {reward.description}
                      </small>
                    </td>
                    <td>
                      <Badge bg="primary" pill>{reward.pointsRequired} pts</Badge>
                    </td>
                    <td>
                      <Badge bg="light" text="dark" className="border">
                        {reward.category || 'N/A'}
                      </Badge>
                    </td>
                    <td>
                      <span className={reward.stock <= 5 ? 'text-danger fw-bold' : ''}>
                        {reward.stock || 0}
                      </span>
                    </td>
                    <td>
                      <Button
                        variant={reward.available ? "outline-success" : "outline-secondary"}
                        size="sm"
                        className="rounded-pill px-3"
                        onClick={() => toggleAvailability(reward._id || reward.id)}
                      >
                        {reward.available ? 'Visible' : 'Masqué'}
                      </Button>
                    </td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <Button variant="light" size="sm" onClick={() => handleEdit(reward)} title="Modifier">
                          ✏️
                        </Button>
                        <Button variant="light" size="sm" className="text-warning" onClick={() => generateRewardQRCode(reward)} title="QR Admin">
                          🏁
                        </Button>
                        <Button variant="light" size="sm" className="text-danger" onClick={() => handleDelete(reward._id || reward.id)} title="Supprimer">
                          🗑️
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* ADD / EDIT MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editingReward ? 'Modifier Récompense' : 'Nouvelle Récompense'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom du cadeau</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: iPhone 15"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Points requis</Form.Label>
                      <Form.Control
                        type="number"
                        name="pointsRequired"
                        value={formData.pointsRequired}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stock initial</Form.Label>
                      <Form.Control
                        type="number"
                        name="stock"
                        value={formData.stock}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Catégorie</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </Form.Select>
                    <Button variant="outline-secondary" onClick={addNewCategory}>+</Button>
                  </div>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Image</Form.Label>
                  <div className="d-flex flex-column align-items-center p-3 border rounded bg-light">
                    {previewImage ? (
                      <img src={previewImage} alt="Preview" className="img-fluid mb-2 rounded" style={{ maxHeight: '150px' }} />
                    ) : (
                      <div className="text-center text-muted col-12 py-4">Aperçu</div>
                    )}
                    <Form.Control
                      type="file"
                      size="sm"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="available-switch"
                    label="Visible par les clients"
                    name="available"
                    checked={formData.available}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit">
              {editingReward ? 'Enregistrer' : 'Créer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* --- OTHER MODALS --- */}

      {/* SCANNER MODAL */}
      <Modal show={showScannerModal} onHide={closeScannerModal} size="lg" centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">📷 Scanner Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!scannerResult ? (
            <div className="text-center">
              <div id="qr-reader" style={{ width: '100%', maxWidth: '400px', margin: '0 auto', borderRadius: '15px', overflow: 'hidden' }}></div>
              <div className="mt-3">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Ou code manuel..."
                  value={manualQRCode}
                  onChange={(e) => setManualQRCode(e.target.value)}
                />
                <Button variant="dark" className="w-100" onClick={handleManualScan}>Valider</Button>
              </div>
            </div>
          ) : (
            <div className={`text-center p-4 rounded-4 ${scannerResult.success ? 'bg-success-subtle' : 'bg-danger-subtle'}`}>
              <div style={{ fontSize: '3rem' }}>{scannerResult.success ? '✅' : '❌'}</div>
              <h4 className="fw-bold mt-2">{scannerResult.message}</h4>
              {scannerResult.redemptionData && (
                <div className="bg-white p-3 rounded-3 mt-3 text-start shadow-sm">
                  <div className="small text-muted">Client</div>
                  <div className="fw-bold">{scannerResult.redemptionData.client?.email}</div>
                  <div className="d-flex justify-content-between mt-2">
                    <div>Points: <span className="text-danger fw-bold">-{scannerResult.redemptionData.client?.pointsDeducted}</span></div>
                    <div>Solde: <span className="text-success fw-bold">{scannerResult.redemptionData.client?.newPoints}</span></div>
                  </div>
                </div>
              )}

              {scannerResult.isClientAuth && (
                <div className="bg-white p-4 rounded-4 mt-3 shadow-sm border border-primary border-opacity-25">
                  <div className="mb-3 text-center">
                    <div className="text-primary small fw-bold text-uppercase mb-1">Client Identifié</div>
                    <div className="h5 fw-bold mb-1">{scannerResult.clientName || scannerResult.clientEmail}</div>
                    <div className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 rounded-pill">
                      Solde actuel : <strong>{scannerResult.currentPoints} pts</strong>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    className="w-100 py-3 rounded-pill fw-bold"
                    onClick={() => handleAddPoints(scannerResult.clientEmail)}
                    disabled={isAddingPoints}
                  >
                    {isAddingPoints ? <Spinner size="sm" /> : '➕ Ajouter des points'}
                  </Button>
                </div>
              )}

              <Button variant="dark" className="mt-3 w-100 rounded-pill" onClick={() => { setScannerResult(null); setTimeout(initializeScanner, 100); }}>
                Scanner un autre
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* QR ADMIN MODAL */}
      <Modal show={showQRCodeModal} onHide={() => setShowQRCodeModal(false)} centered>
        <div className="text-center p-4 bg-white rounded-4">
          <h4 className="fw-bold text-primary mb-3">QR Admin</h4>
          {rewardQRCode?.qrImageUrl && (
            <img src={rewardQRCode.qrImageUrl} className="img-fluid rounded border mb-3" alt="QR" />
          )}
          <div className="alert alert-warning py-1 small">
            Admin seulement
          </div>
          <Button variant="outline-primary" className="w-100 rounded-pill" onClick={() => setShowQRCodeModal(false)}>Fermer</Button>
        </div>
      </Modal>
    </Container >
  );
};

export default ManageRewards;